import React, { ReactElement, useCallback, useState } from "react";

import Tippy from "@tippy.js/react";
import { Instance } from "tippy.js";

import styles from "./MenuList.module.scss";
import { MenuListDivider } from "./MenuListDivider";
import { MenuListItem } from "./MenuListItem";
import "./MenuList.theme.scss";

export interface IMenuList extends React.FC<IMenuListProps> {
  Item: typeof MenuListItem;
  Divider: typeof MenuListDivider;
}

interface IMenuListProps {
  /** Controls visibility of the menu */
  visible?: boolean;
  /** Controls accessability of the menu */
  enabled?: boolean;
  /** The react element that the menu should be anchored */
  anchor: ReactElement;
  /** A callback that is called when the menu is hidden */
  onHidden?: () => void;
  /** A callback that is called when the menu is changed state */
  onTriggered?: (isOpened: boolean) => void;
}

export const MenuList: IMenuList = ({ anchor, visible, enabled, onTriggered, children, ...other }) => {
  const [menuInstance, setMenuInstance] = useState<Instance>();

  const onMenuShow = useCallback(() => {
    onTriggered && onTriggered(true);
  }, [onTriggered]);

  const onMenuHidden = useCallback(() => {
    onTriggered && onTriggered(false);
  }, [onTriggered]);

  const onMenuClick = useCallback(() => {
    menuInstance?.hide();
  }, [menuInstance]);

  const onMenuMount = useCallback((instance: Instance) => {
    setMenuInstance(instance);
  }, []);
  return (
    <Tippy
      appendTo="parent"
      animation="shift-away"
      boundary="scrollParent"
      content={
        <div className={styles.base}>
          <ul className={styles.menuListBody} onClick={onMenuClick}>
            {children}
          </ul>
        </div>
      }
      distance={0}
      duration={200}
      flip={false}
      interactive
      maxWidth={450}
      placement="bottom-start"
      theme="plex-menu-light"
      trigger="click"
      visible={visible}
      enabled={enabled}
      onHide={onMenuHidden}
      onShow={onMenuShow}
      onMount={onMenuMount}
      popperOptions={{
        positionFixed: true
      }}
      {...other}
    >
      {anchor}
    </Tippy>
  );
};

MenuList.Item = MenuListItem;
MenuList.Divider = MenuListDivider;
